/**
 * API kullanımı read model (GET /settings/api-usage, v0.1.164): the contract's shape, re-exported so the screen imports
 * from one place, plus the Turkish words the page uses for states, windows, units and where a number came from.
 */

import type { ApiUsageQuotaSource, ApiUsageQuotaUnit, ApiUsageQuotaWindow, ApiUsageStatus } from '@twin/contracts';

export type {
  ApiUsageDay,
  ApiUsageProviderView,
  ApiUsageQuota,
  ApiUsageQuotaLevel,
  ApiUsageQuotaReported,
  ApiUsageStatus,
  ApiUsageView,
} from '@twin/contracts';

export const API_USAGE_STATUS_LABELS: Readonly<Record<ApiUsageStatus, string>> = Object.freeze({
  connected: 'Bağlı',
  key_missing: 'Anahtar girilmedi',
  off: 'Kapalı',
  not_in_twin: 'Twin’e bağlı değil',
});

/** The window in words, as it reads after a number: "aylık", "bugün". */
export const QUOTA_WINDOW_LABELS: Readonly<Record<ApiUsageQuotaWindow, string>> = Object.freeze({
  day: 'günlük',
  month: 'aylık',
  total: 'toplam',
  '5h': '5 saatlik',
  week: 'haftalık',
});

export const QUOTA_UNIT_LABELS: Readonly<Record<ApiUsageQuotaUnit, string>> = Object.freeze({
  request: 'istek',
  credit: 'kredi',
  usd: '$',
  percent: '%',
});

/** Where a quota came from, in words ("okundu 14:02 · Brave yanıt başlığı"). */
export const QUOTA_SOURCE_LABELS: Readonly<Record<ApiUsageQuotaSource, string>> = Object.freeze({
  response_header: 'yanıt başlığı',
  balance_api: 'bakiye ucu',
  usage_api: 'kullanım ucu',
  manual: 'elle girildi',
  rate_limit_event: 'GM’nin son çalıştırmasındaki sınır bildirimi',
  twin_cap: 'Twin’in kendi sınırı',
});

/**
 * The last error in plain words ("Son hata: yanıt gelmedi (zaman aşımı)"). The kind is the meter's one word
 * (timeout, network, abort, error, http_<status>); the page keeps it in a title for whoever needs the code.
 */
export function lastErrorText(kind: string): string {
  switch (kind) {
    case 'timeout':
      return 'yanıt gelmedi (zaman aşımı)';
    case 'network':
      return 'bağlantı kurulamadı';
    case 'abort':
      return 'yarıda kesildi';
    case 'unauthorized':
    case 'http_401':
    case 'http_403':
      return 'anahtar reddedildi';
    case 'http_429':
      return 'çok fazla istek (sınır)';
    default: {
      const status = /^http_(\d{3})$/.exec(kind)?.[1];
      if (status === undefined) return 'hata';
      return status.startsWith('5') ? 'sağlayıcıda arıza' : `sağlayıcı isteği kabul etmedi (${status})`;
    }
  }
}
